<template>
  <v-row>
    <v-col>
      <v-card class="pa-10 rounded-lg fill-height">
        <v-row no-gutters>
          <v-col
            cols="auto"
          >
            <v-avatar
              size="120px"
              class="elevation-1"
            >
              <v-img
                :src="player.photo_url"
                width="120px"
                height="120px"
                class="rounded"
              >
                <template v-slot:placeholder>
                  <v-icon x-large>
                    fal fa-user
                  </v-icon>
                </template>
              </v-img>
            </v-avatar>
          </v-col>
          <v-col
            class="px-6"
          >
            <div class="mb-3">
              <v-row
                align="center"
                class="title"
              >
                <v-col
                  class="grey--text text--darken-4"
                >
                  {{ player.first_name }} {{ player.last_name }}
                </v-col>
                <v-col
                  cols="auto"
                  class="px-3"
                >
                  <v-select
                    :value="player.player_status_id"
                    :items="playerStatuses.data"
                    item-value="id"
                    item-text="name"
                    dense
                    style="max-width: 170px"
                    solo
                    flat
                    hide-details
                    @change="savePlayerStatus($event)"
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip
                        label
                        :color="item.name === 'healthy' ? 'success' : 'error lighten-2'"
                        :dark="item.name === 'healthy'"
                      >
                        <span style="max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                          {{ $t(`player_status.${item.name}`) }}
                        </span>
                      </v-chip>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t(`player_status.${item.name}`) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <div>
                {{ player.email }}
              </div>
              <div>
                {{ player.phone }}
              </div>
            </div>
            <div class="d-flex">
              <div class="mr-10">
                <div>
                  {{ $t('individual_analysis.data.date_of_birth') }}: {{ $dayjs(player.birth_date).format('L') }}
                </div>
                <div>
                  {{ $t('individual_analysis.data.age') }}: {{ playerAge }}
                </div>
              </div>
              <div>
                <div
                  class="text-capitalize"
                >
                  {{ $t('individual_analysis.data.position') }}: {{ player.player_positions && player.player_positions.length > 0 ? player.player_positions.map(p => $t(`player_position.${toCamelCase(p.name)}`)).join(', ') : '-' }}
                </div>
                <div>
                  {{ $t('individual_analysis.data.team') }}: {{ player.team ? player.team.name : '-' }}
                </div>
              </div>
            </div>
            <div class="font-weight-bold mt-4">
              <v-btn
                outlined
                class="text-none"
                @click="$emit('edit:hr-profile')"
              >
                {{ $t('button.manage_hr_profile') }}
              </v-btn>
            </div>
            <div
              v-if="hasItlModule"
              class="mt-4"
            >
              <v-switch
                v-model="player.disable_next_morning_feedback"
                inset
                hide-details
                class="d-inline-block"
                :label="$t('individual_analysis.switch.disable_itl_feedback')"
                @change="toggleNextMorningFeedback"
              />
            </div>
          </v-col>
        </v-row>
        <div
          v-if="$can('view-player-notes') && player.notes.length > 0"
          class="mt-8"
        >
          <v-alert
            v-for="note in player.notes.filter(n => n.is_pinned)"
            :key="note.id"
            outlined
            text
            color="orange lighten-3"
            class="mb-1 px-2 py-1 caption"
          >
            <v-row
              no-gutters
              class="px-3"
            >
              <v-col
                cols="2"
                class="grey--text"
              >
                {{ note.created_by_user.first_name }} {{ note.created_by_user.last_name }}
              </v-col>
              <v-col
                class="note-body grey--text text--darken-3"
                v-html="note.text"
              />
              <v-col
                cols="auto"
                class="grey--text"
              >
                {{ $dayjs(note.updated_at).format('D MMM YYYY') }}
              </v-col>
            </v-row>
          </v-alert>
        </div>
      </v-card>
    </v-col>
    <v-col
      v-if="hasItlModule && showWellnessData"
      cols="auto"
    >
      <v-card class="pa-6 rounded-lg fill-height">
        <v-card-title class="pl-2">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.wellness_status') }}
          </div>
        </v-card-title>
        <v-card-text class="pt-12 pa-0 text-center">
          <v-progress-circular
            :value="player.latest_stats.player_wellness"
            :size="150"
            :color="player.latest_stats.player_wellness >= 90 ? 'green' : (player.latest_stats.player_wellness > 70 ? 'orange' : 'red')"
            :width="16"
            :rotate="-90"
          >
            <div class="text-h5 font-weight-bold">
              {{ player.latest_stats.player_wellness }}%
            </div>
          </v-progress-circular>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col
      v-if="hasItlModule"
      cols="auto"
    >
      <v-card class="pa-6 rounded-lg fill-height">
        <v-card-title>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.pain_status') }}
          </div>
        </v-card-title>
        <v-card-text class="pt-6">
          <human-body-female-front-muscles
            v-if="player.gender === 'female'"
            style="height: 250px; width: 160px"
            :stats="player.latest_stats"
          />
          <human-body-front-muscles
            v-else
            style="height: 250px; width: 160px"
            :stats="player.latest_stats"
          />
          <human-body-female-back-muscles
            v-if="player.gender === 'female'"
            style="height: 250px; width: 160px"
            :stats="player.latest_stats"
          />
          <human-body-back-muscles
            v-else
            style="height: 250px; width: 160px"
            :stats="player.latest_stats"
          />
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles'
import HumanBodyHydration from '@/components/human-body/HumanBodyHydration'
import { PlayerApiService, PlayerStatusApiService } from '@/data/services'
import HumanBodyFemaleFrontMuscles from '@/components/human-body/HumanBodyFemaleFrontMuscles'
import HumanBodyFemaleBackMuscles from '@/components/human-body/HumanBodyFemaleBackMuscles'
import { getColorForValue, toCamelCase } from '@/utils/helpers'

export default {
  name: 'PlayerDetailsCard',
  // eslint-disable-next-line vue/no-unused-components
  components: { HumanBodyFemaleBackMuscles, HumanBodyFemaleFrontMuscles, HumanBodyHydration, HumanBodyBackMuscles, HumanBodyFrontMuscles },
  props: {
    player: {
      type: Object,
      required: true
    },
    stats: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      playerStatuses: {
        data: [],
        loading: false,
        loaded: false
      },
      playerStatusId: null,
      savingPlayerStatus: false
    }
  },
  computed: {
    playerAge() {
      if (!this.player.birth_date) return ''

      return this.$dayjs().diff(this.$dayjs(this.player.birth_date), 'year')
    },
    formattedPlayerPreviousClubString() {
      if (!this.player.clubHistory) return ''

      const clubs = this.player.clubHistory.split(';')

      if (!clubs.length) return ''

      return clubs[0]
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    showWellnessData() {
      return !this.user.organisation_settings.has_short_wellness_feedback
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    }
  },
  watch: {
    'player.player_status_id': (newValue) => {

    }
  },
  created() {
    this.fetchPlayerStatuses()
  },
  methods: {
    toCamelCase,
    getColorForValue,
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async savePlayerStatus(playerStatusId) {
      try {
        this.savingPlayerStatus = true

        await PlayerApiService.updateStatus(this.player.id, {
          player_status_id: playerStatusId
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_status_update.success'),
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_status_update.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.savingPlayerStatus = false
      }
    },
    async toggleNextMorningFeedback(value) {
      try {
        await PlayerApiService.updateStatus(this.player.id, {
          disable_next_morning_feedback: value
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_status_update.error'),
          color: 'error'
        })
        throw e
      }
    }

  }
}
</script>
